import React, { useState, useEffect } from 'react';
import { betsAPI } from '../services/api';
import { Bet, UserStats } from '../types';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FaTrophy, FaTimesCircle, FaClock, FaBan, FaChartLine, FaWallet, FaPercent, FaLink } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useCurrencyStore } from '../store';
import { formatMoney } from '../utils/currency';

const STATUS_CONFIG = {
  GANADA: { color: 'text-emerald-400', bg: 'bg-emerald-400/10', icon: FaTrophy, label: 'Ganada' },
  PERDIDA: { color: 'text-red-400', bg: 'bg-red-400/10', icon: FaTimesCircle, label: 'Perdida' },
  ACTIVA: { color: 'text-yellow-400', bg: 'bg-yellow-400/10', icon: FaClock, label: 'Activa' },
  CANCELADA: { color: 'text-slate-400', bg: 'bg-slate-400/10', icon: FaBan, label: 'Cancelada' },
  PENDIENTE: { color: 'text-blue-400', bg: 'bg-blue-400/10', icon: FaClock, label: 'Pendiente' }
};

const BetAmounts: React.FC<{ bet: Bet }> = ({ bet }) => {
  const { currency, rate } = useCurrencyStore();
  return (
    <div className="text-right flex-shrink-0">
      <div className="text-lg font-bold text-white">{formatMoney(bet.cantidad_apostada, currency, rate)}</div>
      <div className="text-xs text-slate-400">apostado</div>
      {bet.estado === 'ACTIVA' && (
        <div className="text-xs text-emerald-400 mt-1">+{formatMoney(bet.ganancia_potencial, currency, rate)} posible</div>
      )}
      {bet.estado === 'GANADA' && bet.ganancia_perdida && (
        <div className="text-xs text-emerald-400 font-bold mt-1">+{formatMoney(bet.ganancia_perdida, currency, rate)} ganado</div>
      )}
      {bet.estado === 'PERDIDA' && bet.ganancia_perdida && (
        <div className="text-xs text-red-400 font-bold mt-1">{formatMoney(bet.ganancia_perdida, currency, rate)}</div>
      )}
    </div>
  );
};

const parseParlayPicks = (seleccion: any): any[] => {
  try {
    if (Array.isArray(seleccion)) return seleccion;
    if (typeof seleccion === 'string') {
      const trimmed = seleccion.trim();
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
    }
    if (seleccion && typeof seleccion === 'object' && !Array.isArray(seleccion)) {
      const vals = Object.values(seleccion);
      if (vals.length > 0) return vals as any[];
    }
  } catch { /* ignore */ }
  return [];
};

const ParlayBetRow: React.FC<{ bet: Bet; onCancel: (id: string) => void }> = ({ bet, onCancel }) => {
  const { currency, rate } = useCurrencyStore();
  const [expanded, setExpanded] = useState(false);
  const config = STATUS_CONFIG[bet.estado] || STATUS_CONFIG.ACTIVA;
  const Icon = config.icon;
  const picks = parseParlayPicks(bet.seleccion);
  const cuota = parseFloat(String(bet.cuota_bloqueada || 0));

  return (
    <div className="card mb-3 animate-fade-in border border-emerald-500/20">
      {/* Header row — always visible */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${config.color} ${config.bg}`}>
              <Icon size={10} />
              {config.label}
            </span>
            <span className="flex items-center gap-1 text-xs text-emerald-400 font-semibold">
              <FaLink size={10} />
              PARLAY
            </span>
            <span className="text-xs text-yellow-400 font-mono font-bold">{cuota.toFixed(2)}x</span>
            <span className="text-xs text-slate-500">
              {picks.length > 0 ? `${picks.length} selecciones` : 'combinada'}
            </span>
          </div>

          {/* Summary line */}
          <div className="text-xs text-slate-400 truncate">
            {picks.length > 0
              ? picks.map((p: any) => p.seleccion || p.descripcion).join(' · ')
              : bet.descripcion}
          </div>

          {/* Toggle button */}
          <button
            onClick={() => setExpanded(e => !e)}
            className="mt-2 flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors font-medium"
          >
            {expanded ? '▲ Ocultar selecciones' : '▼ Ver selecciones'}
          </button>
        </div>
        <BetAmounts bet={bet} />
      </div>

      {/* Expanded picks */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-700/60 space-y-2">
          {picks.length > 0 ? (
            picks.map((pick: any, i: number) => {
              const pickRes = pick.resultado;
              const pickBg = pickRes === 'GANADA' ? 'bg-emerald-900/30 border border-emerald-500/30'
                           : pickRes === 'PERDIDA' ? 'bg-red-900/30 border border-red-500/30'
                           : 'bg-slate-800/70';
              return (
                <div key={i} className={`rounded-lg px-3 py-2.5 ${pickBg}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs text-slate-400 truncate flex-1">
                      {pick.match_label || pick.descripcion || `Pick ${i + 1}`}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {pickRes === 'GANADA' && <span className="text-xs text-emerald-400 font-bold">✓ Ganada</span>}
                      {pickRes === 'PERDIDA' && <span className="text-xs text-red-400 font-bold">✗ Perdida</span>}
                      {(!pickRes || pickRes === 'PENDIENTE') && <span className="text-xs text-yellow-400">⏳ Pendiente</span>}
                      <span className="text-xs text-yellow-400 font-mono font-bold">
                        {parseFloat(String(pick.cuota || 1)).toFixed(2)}x
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">
                      {pick.tipo}
                    </span>
                    <strong className="text-sm text-white">{pick.seleccion}</strong>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-xs text-slate-500 italic">
              No se pueden mostrar los detalles de este parlay.
            </p>
          )}

          <div className="flex items-center justify-between pt-1 text-xs text-slate-400">
            <span>Cuota combinada:</span>
            <span className="text-yellow-400 font-mono font-bold text-sm">{cuota.toFixed(2)}x</span>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>Ganancia posible:</span>
            <span className="text-emerald-400 font-bold">
              {formatMoney(bet.ganancia_potencial || 0, currency, rate)}
            </span>
          </div>
        </div>
      )}

      {bet.estado === 'ACTIVA' && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          <button onClick={() => onCancel(bet.id)} className="text-xs text-red-400 hover:text-red-300 transition-colors">
            Cancelar parlay
          </button>
        </div>
      )}
    </div>
  );
};

const BetRow: React.FC<{ bet: Bet; onCancel: (id: string) => void }> = ({ bet, onCancel }) => {
  if (bet.tipo_apuesta === 'PARLAY') return <ParlayBetRow bet={bet} onCancel={onCancel} />;

  const config = STATUS_CONFIG[bet.estado] || STATUS_CONFIG.ACTIVA;
  const Icon = config.icon;

  return (
    <div className="card mb-3 animate-fade-in">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${config.color} ${config.bg}`}>
              <Icon size={10} />
              {config.label}
            </span>
            <span className="text-xs text-slate-500">{bet.tipo_apuesta}</span>
            {bet.deporte && <span className="text-xs text-slate-600">{bet.deporte}</span>}
          </div>
          <h3 className="font-medium text-white text-sm">
            {bet.local_nombre} vs {bet.visitante_nombre}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">{bet.descripcion}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
            <span>Selección: <strong className="text-slate-200">{bet.seleccion}</strong></span>
            <span>Cuota: <strong className="text-yellow-400">{parseFloat(String(bet.cuota_bloqueada || 0)).toFixed(2)}</strong></span>
            {bet.fecha_hora && (
              <span>{new Date(bet.fecha_hora).toLocaleDateString('es-CO')}</span>
            )}
          </div>
        </div>
        <BetAmounts bet={bet} />
      </div>

      {bet.estado === 'ACTIVA' && (
        <div className="mt-3 pt-3 border-t border-slate-700">
          <button
            onClick={() => onCancel(bet.id)}
            className="text-xs text-red-400 hover:text-red-300 transition-colors"
          >
            Cancelar apuesta
          </button>
        </div>
      )}
    </div>
  );
};

const MyBets: React.FC = () => {
  const { currency, rate } = useCurrencyStore();
  const [bets, setBets] = useState<Bet[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const TABS = [
    { value: '', label: 'Todas' },
    { value: 'ACTIVA', label: 'Activas' },
    { value: 'GANADA', label: 'Ganadas' },
    { value: 'PERDIDA', label: 'Perdidas' },
    { value: 'CANCELADA', label: 'Canceladas' }
  ];

  useEffect(() => {
    loadData();
  }, [activeTab, page]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [betsRes, statsRes] = await Promise.all([
        betsAPI.getMyBets({ estado: activeTab || undefined, page, limit: 20 }),
        betsAPI.getStats()
      ]);
      setBets(betsRes.data.bets || []);
      setTotal(betsRes.data.total || 0);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Error al cargar apuestas');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('¿Cancelar esta apuesta?')) return;
    try {
      await betsAPI.cancel(id);
      toast.success('Apuesta cancelada');
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al cancelar');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-20 px-4 pb-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Mis Apuestas</h1>
          <p className="text-slate-400 text-sm">Historial y estadísticas de tus apuestas</p>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="stat-card">
              <FaWallet className="text-indigo-400 mx-auto mb-2" size={20} />
              <div className="text-xl font-bold text-white">{formatMoney(stats.balance || 0, currency, rate)}</div>
              <div className="text-xs text-slate-400">Balance</div>
            </div>
            <div className="stat-card">
              <FaPercent className="text-yellow-400 mx-auto mb-2" size={20} />
              <div className="text-xl font-bold text-white">{parseFloat(String(stats.ratio_acierto || 0)).toFixed(1)}%</div>
              <div className="text-xs text-slate-400">Aciertos</div>
            </div>
            <div className="stat-card">
              <FaChartLine className={`mx-auto mb-2 ${parseFloat(String(stats.roi)) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} size={20} />
              <div className={`text-xl font-bold ${parseFloat(String(stats.roi)) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {parseFloat(String(stats.roi || 0)) >= 0 ? '+' : ''}{parseFloat(String(stats.roi || 0)).toFixed(1)}%
              </div>
              <div className="text-xs text-slate-400">ROI</div>
            </div>
            <div className="stat-card">
              <FaTrophy className="text-emerald-400 mx-auto mb-2" size={20} />
              <div className="text-xl font-bold text-white">{stats.ganadas}/{stats.total}</div>
              <div className="text-xs text-slate-400">Ganadas/Total</div>
            </div>
          </div>
        )}

        {/* Extended Stats */}
        {stats && (
          <div className="card mb-6 grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-slate-400 text-xs mb-1">Total Apostado</div>
              <div className="font-semibold text-white">{formatMoney(stats.total_apostado, currency, rate)}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs mb-1">Total Ganado</div>
              <div className="font-semibold text-emerald-400">{formatMoney(stats.total_ganado, currency, rate)}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs mb-1">Total Perdido</div>
              <div className="font-semibold text-red-400">{formatMoney(Math.abs(parseFloat(String(stats.total_perdido))), currency, rate)}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-slate-800 pb-2">
          {TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => { setActiveTab(tab.value); setPage(1); }}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${activeTab === tab.value ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Bets List */}
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-4 bg-slate-700 rounded w-1/3 mb-2" />
                <div className="h-5 bg-slate-700 rounded w-2/3 mb-2" />
                <div className="h-3 bg-slate-700 rounded w-full" />
              </div>
            ))}
          </div>
        ) : bets.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">📋</div>
            <p className="text-slate-400">No hay apuestas en esta categoría</p>
          </div>
        ) : (
          <>
            {bets.map(bet => <BetRow key={bet.id} bet={bet} onCancel={handleCancel} />)}
            {total > bets.length && (
              <div className="text-center mt-4">
                <button onClick={() => setPage(p => p + 1)} className="btn-secondary">
                  Cargar más ({bets.length}/{total})
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyBets;
